import React, { useCallback, useEffect } from 'react';
import { Icon, Tappable } from '@vectord/components';
import cogoToast from 'cogo-toast';
import classNames from 'classnames';

import { DEFAULT_ERROR_MESSAGE } from 'data/defaultErrorMessage';
import {
  bulkUpdateSyncTypeSelector,
  EDIT_CLASS_PAGE_TRACKINGS,
  useBulkUpdateLectureTemplatesMutation,
} from 'features/classCreation';
import analytics from 'utils/analytics';
import GlobalLoader from 'components/GlobalLoader';
import styles from './BulkUpdateSyncConfirmButton.module.scss';
import useAppSelector from 'hooks/useAppSelector';

export default function BulkUpdateSyncConfirmButton({
  academyTopic,
  handleClose,
}: IBulkUpdateSyncConfirmButton) {
  const bulkUpdateSyncType = useAppSelector(bulkUpdateSyncTypeSelector);
  const [
    bulkUpdateLectureTemplates,
    { error, isLoading: isSyncing, isSuccess },
  ] = useBulkUpdateLectureTemplatesMutation();

  const handleConfirm = useCallback(() => {
    if (isSyncing) return;

    analytics.click({
      click_type: EDIT_CLASS_PAGE_TRACKINGS.classTemplateBulkUpdate,
      custom: {
        academyTopic: academyTopic,
        bulkUpdateSyncType: bulkUpdateSyncType,
      },
    });

    bulkUpdateLectureTemplates({
      academyTopicId: academyTopic.id,
      bulkUpdateSyncType,
    });
  }, [isSyncing, bulkUpdateLectureTemplates, academyTopic, bulkUpdateSyncType]);

  useEffect(() => {
    if (isSuccess) {
      cogoToast.success(
        'Lecture Template Updates have been synced successfully! Check in few minutes'
      );
      handleClose();
    }
  }, [handleClose, isSuccess]);

  useEffect(() => {
    if (error) {
      cogoToast.error(
        (error as IError)?.data?.message || DEFAULT_ERROR_MESSAGE
      );
    }
  }, [error]);

  return (
    <>
      <GlobalLoader isVisible={isSyncing} />
      <div className={classNames('m-v-s', styles.actionFooter)}>
        <Tappable
          className="primary h5 m-r-m m-v-xxs"
          disabled={isSyncing}
          noHighlight
          onClick={handleClose}
        >
          Cancel
        </Tappable>
        <Tappable
          className="btn btn-primary"
          disabled={isSyncing}
          onClick={handleConfirm}
        >
          <Icon
            name="double-refresh"
            className="m-r-xxs"
          />
          Sync Now
        </Tappable>
      </div>
    </>
  );
}
